import type { ReactNode } from 'react';
import { Header } from '../components/Header.js';
import { Footer } from '../components/Footer.js';
import type { CurrencyCode } from '../hooks/useCurrency.js';
import './Legal.css';

/**
 * The shell every counsel-cleared policy page renders inside.
 *
 * ── Why a shell rather than a fourth copy of `Legal.tsx` ──────────────────────────────────────
 *
 * [B-271](../../../../docs/platform/TODO.md): four documents in `docs/landing/` are marked
 * *"Yes — publish as-is"* in that folder's own table, counsel-cleared on 2026-08-08, and **none has
 * ever rendered on the live site** — `App.tsx` had four views and none was any of them. The row
 * prescribes *"the same hand-transcription-into-JSX treatment `Legal.tsx` establishes"*, and this is
 * that treatment with the chrome extracted once: `Legal.tsx` carries ~40 lines of header, breadcrumb,
 * meta chips and footer before its first sentence, and copying those four more times would mean five
 * places to fix a nav change.
 *
 * ⚠️ **`Legal.tsx` is deliberately NOT migrated onto this.** It is live, counsel-cleared and
 * working; rewriting a legal page to prove a refactor is the kind of change that quietly alters
 * published wording. New pages use the shell; the existing one is left alone.
 *
 * ⚠️ **Content stays hand-transcribed, per the established pattern**, rather than rendered from the
 * markdown. The reasoning `Legal.tsx` records is that a change to the source doc must then be
 * mirrored **deliberately** rather than by a build step nobody wrote — and for legally-cleared text
 * an automatic pipeline is the more dangerous default, because a doc edit would publish itself.
 */
export interface PolicyPageProps {
  /** Breadcrumb section, e.g. "Trust". */
  section: string;
  /** Breadcrumb leaf and `<h1>` — one string, so they cannot disagree. */
  title: string;
  /** The standfirst under the heading. */
  intro: ReactNode;
  /** The date the document was reviewed, exactly as `docs/landing/` records it. */
  reviewed: string;
  /**
   * Whether counsel cleared this document.
   *
   * ⚠️ Not defaulted to `true`. The chip is a claim about a legal review, and a page that asserts
   * one it never had is worse than a page with no chip — so each caller states it.
   */
  counselCleared: boolean;
  children: ReactNode;

  ccyCode: CurrencyCode;
  regionLabel: string;
  onCycleCurrency: () => void;
  onGoHome: () => void;
  onGoDocs: () => void;
  onGoConnectorsDocs: () => void;
  onGoPricing: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
}

export function PolicyPage({
  section,
  title,
  intro,
  reviewed,
  counselCleared,
  children,
  ccyCode,
  regionLabel,
  onCycleCurrency,
  onGoHome,
  onGoDocs,
  onGoConnectorsDocs,
  onGoPricing,
  onOpenAudit,
  onOpenBooking,
  onToggleHelp,
}: PolicyPageProps) {
  return (
    <div className="land-legal">
      <Header
        view="legal"
        ccyCode={ccyCode}
        regionLabel={regionLabel}
        onCycleCurrency={onCycleCurrency}
        onGoHome={onGoHome}
        onGoDocs={onGoDocs}
        onOpenAudit={onOpenAudit}
        onOpenBooking={onOpenBooking}
        onToggleHelp={onToggleHelp}
      />
      <main className="land-legal__layout land-container">
        <article className="land-docs__article land-legal__article">
          <div className="land-docs__breadcrumb">
            <span>{section}</span>
            <span>/</span>
            <span className="land-docs__breadcrumb-current">{title}</span>
          </div>
          <h1>{title}</h1>
          <p className="land-docs__intro">{intro}</p>
          <div className="land-docs__meta">
            <span className="land-docs__chip">reviewed {reviewed}</span>
            {counselCleared ? (
              <span className="land-docs__chip land-docs__chip--tint">counsel-cleared</span>
            ) : null}
          </div>
          {children}
        </article>
      </main>
      {/* `onGoConnectorsDocs` is threaded for the Header's sake; the Footer takes the same two
          links `Legal.tsx` passes it. */}
      <Footer onGoDocs={onGoDocs} onGoPricing={onGoPricing} />
    </div>
  );
}
